// src/pages/ToolkitPage.jsx - Relationship Compass
import React, { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import {
  Heart,
  TrendingUp,
  Gift,
  Target,
  Scale,
  MessageCircle,
  Compass,
  Eye,
  Wind,
  AlertTriangle,
  Shield,
  Wrench,
  Flame,
  Layers,
  HeartHandshake,
  ChevronLeft,
  Clock,
  Award,
  BookOpen,
  ArrowRight,
  Brain,
  Zap,
  FileText,
  Map,
  Calendar,
  Users
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';

// Lazy load relationship tools for performance
// Module 01 - Appreciation & Connection
const AppreciationBuilderMod01 = lazy(() => import('../components/tools/mod01/AppreciationBuilder'));
const RatioTrackerMod01 = lazy(() => import('../components/tools/mod01/RatioTracker'));
const ReceivingPracticeMod01 = lazy(() => import('../components/tools/mod01/ReceivingPractice'));

// Module 02 - Understanding Emotions
const AttunementTracker = lazy(() => import('../components/tools/mod02/AttunementTracker'));
const FeelingWheel = lazy(() => import('../components/tools/mod02/FeelingWheel'));
const ValidationLab = lazy(() => import('../components/tools/mod02/ValidationLab'));

// Module 03 - Communication Skills
const ConversationNavigator = lazy(() => import('../components/tools/mod03/ConversationNavigator'));
const FourHorsemenDetector = lazy(() => import('../components/tools/mod03/FourHorsemenDetector'));
const LAERTrainer = lazy(() => import('../components/tools/mod03/LAERTrainer'));

// Module 04 - Conflict Resolution
const ConflictIdentifier = lazy(() => import('../components/tools/mod04/ConflictIdentifier'));
const FloodingMonitor = lazy(() => import('../components/tools/mod04/FloodingMonitor'));
const RepairBuilder = lazy(() => import('../components/tools/mod04/RepairBuilder'));

// Module 05 - Intimacy & Vulnerability
const DesireNavigator = lazy(() => import('../components/tools/mod05/DesireNavigator'));
const FiveDimensionsTracker = lazy(() => import('../components/tools/mod05/FiveDimensionsTracker'));
const VulnerabilityCoach = lazy(() => import('../components/tools/mod05/VulnerabilityCoach'));

// Module 06 - Trust
const TrustBuilder = lazy(() => import('../components/tools/mod06/TrustBuilder'));
const TrustErosionWarning = lazy(() => import('../components/tools/mod06/TrustErosionWarning'));
const TrustAgreementBuilder = lazy(() => import('../components/tools/mod06/TrustAgreementBuilder'));

// Module 07 - Growing Together
const GrowthSupportNavigator = lazy(() => import('../components/tools/mod07/GrowthSupportNavigator'));
const SharedVisionBuilder = lazy(() => import('../components/tools/mod07/SharedVisionBuilder'));

// Module 08 - Resilience
const RelationshipCompass = lazy(() => import('../components/tools/mod08/RelationshipCompass'));
const RitualsBuilder = lazy(() => import('../components/tools/mod08/RitualsBuilder'));
const ResilienceNavigator = lazy(() => import('../components/tools/mod08/ResilienceNavigator'));

const ToolkitPage = () => {
  const [activeTool, setActiveTool] = useState(null);
  const { toolsUsage = {} } = useApp();

  const tools = [
    // ===== MODULE 01: Appreciation & Connection =====
    {
      id: 'appreciation-builder-mod01',
      title: 'Appreciation Builder',
      subtitle: 'Build Specific, Meaningful Appreciations',
      description: 'Master the three-part appreciation formula: specific behaviour + impact on you + what it reveals about their character. Build the 5:1 positive-to-negative ratio Gottman found in couples who stayed together.',
      icon: Heart,
      heroImage: '/images/tools/appreciation-builder.jpg',
      color: 'from-rose-500 to-pink-500',
      category: 'Module 1',
      estimatedTime: '5-10 mins',
      component: AppreciationBuilderMod01,
      module: 1,
      priority: 1,
      features: [
        'Three-part appreciation formula (behaviour + impact + value)',
        'Save appreciations and track delivery',
        'Gottman 5:1 ratio guidance',
        'Practise both giving and receiving appreciation'
      ],
      whenToUse: 'When you want to strengthen connection through specific, meaningful appreciation rather than generic praise'
    },
    {
      id: 'ratio-tracker-mod01',
      title: '5:1 Ratio Tracker',
      subtitle: "Track Gottman's Magic Ratio",
      description: 'Track your daily positive-to-negative interaction ratio. Gottman found that couples who stayed together showed about five positive interactions for every negative one when they disagreed. Monitor your ratio and build the positivity reserve that helps relationships weather storms.',
      icon: TrendingUp,
      heroImage: '/images/tools/ratio-tracker.jpg',
      color: 'from-emerald-500 to-teal-500',
      category: 'Module 1',
      estimatedTime: '2-5 mins/day',
      component: RatioTrackerMod01,
      module: 1,
      priority: 2,
      features: [
        'Daily positive and negative interaction tracking',
        '5:1 ratio calculation and visualisation',
        'Weekly trend analysis',
        'Pattern insights and recommendations'
      ],
      whenToUse: 'Daily tracking to keep positives well ahead of negatives'
    },
    {
      id: 'receiving-practice-mod01',
      title: 'Receiving Practice',
      subtitle: 'Learn to Receive Appreciation Gracefully',
      description: "Many of us deflect, dismiss, or downplay appreciation. Practise receiving it fully: with eye contact, genuine acknowledgement, and without deflection. This completes the appreciation loop and deepens connection.",
      icon: Gift,
      heroImage: '/images/tools/receiving-practice.jpg',
      color: 'from-purple-500 to-pink-500',
      category: 'Module 1',
      estimatedTime: '5 mins',
      component: ReceivingPracticeMod01,
      module: 1,
      priority: 3,
      features: [
        'Common deflection patterns (dismissing, deflecting, reciprocating too quickly)',
        'Graceful receiving framework',
        'Practice scenarios with feedback',
        'Track your receiving habits over time'
      ],
      whenToUse: 'When you notice you deflect appreciation or struggle to receive compliments without discomfort'
    },

    // ===== MODULE 02: Understanding Emotions =====
    {
      id: 'feeling-wheel-mod02',
      title: 'Feeling Wheel',
      subtitle: 'Expand Your Emotional Vocabulary',
      description: 'Move beyond "fine," "good," and "stressed." Use the Feeling Wheel to identify precisely what you\'re experiencing, from the core emotions (joy, sadness, anger, fear) to 100+ specific feelings. Precision creates connection.',
      icon: Target,
      heroImage: '/images/tools/feeling-wheel.jpg',
      color: 'from-blue-500 to-cyan-500',
      category: 'Module 2',
      estimatedTime: '5-10 mins',
      component: FeelingWheel,
      module: 2,
      priority: 1,
      features: [
        'Interactive feeling wheel with 6 core emotions',
        '100+ specific feeling words organised by intensity',
        'Daily emotion tracking and journaling',
        'Pattern insights on your emotional landscape'
      ],
      whenToUse: 'When you struggle to name what you\'re feeling, or when "fine" becomes your default response'
    },
    {
      id: 'attunement-tracker-mod02',
      title: 'Attunement Tracker',
      subtitle: 'Recognise and Respond to Emotional Bids',
      description: 'Track how often you recognise your partner\'s emotional state and respond with validation. Attunement (accurately reading and responding to emotions) is the foundation of feeling seen and understood.',
      icon: Eye,
      heroImage: '/images/tools/attunement-tracker.jpg',
      color: 'from-indigo-500 to-purple-500',
      category: 'Module 2',
      estimatedTime: '5 mins/day',
      component: AttunementTracker,
      module: 2,
      priority: 2,
      features: [
        'Daily attunement check-ins',
        'Track accuracy: Did you read their emotion correctly?',
        'Response quality: Did you validate or dismiss?',
        'Weekly attunement score and improvement tracking'
      ],
      whenToUse: 'When you want to build the skill of accurately reading and responding to your partner\'s emotional state'
    },
    {
      id: 'validation-lab-mod02',
      title: 'Validation Lab',
      subtitle: 'Master Validation Without Agreement',
      description: 'Validation isn\'t agreement. It\'s acknowledging that someone\'s feelings make sense given their experience. Practise the four levels of validation: listening, acknowledging, understanding, and normalising.',
      icon: Shield,
      heroImage: '/images/tools/validation-lab.jpg',
      color: 'from-teal-500 to-emerald-500',
      category: 'Module 2',
      estimatedTime: '10-15 mins',
      component: ValidationLab,
      module: 2,
      priority: 3,
      features: [
        'Four levels of validation (listen, acknowledge, understand, normalise)',
        'Practice scenarios with real-time feedback',
        'Common invalidation patterns to avoid',
        'Track your validation skill development'
      ],
      whenToUse: 'When conversations escalate because your partner feels dismissed, even when you don\'t intend it'
    },
    {
      id: 'four-horsemen-detector-mod03',
      title: 'The Four Horsemen Detector',
      subtitle: 'Identify Relationship-Destroying Patterns',
      description: 'Gottman identified four communication patterns (criticism, contempt, defensiveness, and stonewalling) that were among the strongest predictors of later divorce in his research. Learn to recognise them in real-time and choose healthier alternatives.',
      icon: AlertTriangle,
      heroImage: '/images/tools/four-horsemen.jpg',
      color: 'from-red-500 to-orange-500',
      category: 'Module 3',
      estimatedTime: '10 mins',
      component: FourHorsemenDetector,
      module: 3,
      priority: 1,
      features: [
        'Identify all four horsemen with examples',
        'Log instances in your conversations',
        'Learn the antidotes (complaint, appreciation, responsibility, self-soothing)',
        'Track progress in reducing destructive patterns'
      ],
      whenToUse: 'When conversations escalate into criticism, contempt, defensiveness, or stonewalling'
    },
    {
      id: 'laer-trainer-mod03',
      title: 'LAER Trainer',
      subtitle: 'Listen, Acknowledge, Explore, Respond',
      description: 'Master the four-step framework for difficult conversations: Listen fully, Acknowledge their experience, Explore with curiosity, Respond thoughtfully. Turn conflicts into connection.',
      icon: Compass,
      heroImage: '/images/tools/laer-trainer.jpg',
      color: 'from-blue-500 to-indigo-500',
      category: 'Module 3',
      estimatedTime: '15 mins',
      component: LAERTrainer,
      module: 3,
      priority: 2,
      features: [
        'Four-step LAER framework with examples',
        'Practice scenarios with real-time feedback',
        'Common mistakes at each step',
        'Track your LAER skill development'
      ],
      whenToUse: 'Before or during difficult conversations where you want to maintain connection whilst navigating disagreement'
    },
    {
      id: 'conversation-navigator-mod03',
      title: 'Conversation Navigator',
      subtitle: 'Navigate Difficult Conversations',
      description: 'Plan and navigate conversations about money, sex, parenting, in-laws, and other hot-button topics. Use structured prompts to stay grounded when emotions run high.',
      icon: MessageCircle,
      heroImage: '/images/tools/conversation-navigator.jpg',
      color: 'from-purple-500 to-pink-500',
      category: 'Module 3',
      estimatedTime: '10-20 mins',
      component: ConversationNavigator,
      module: 3,
      priority: 3,
      features: [
        'Pre-conversation planning templates',
        'Hot-button topic frameworks (money, sex, parenting, etc.)',
        'In-conversation grounding prompts',
        'Post-conversation reflection and learning'
      ],
      whenToUse: 'When you need to have a difficult conversation and want to stay grounded and connected'
    },

    // ===== MODULE 04: Conflict Resolution =====
    {
      id: 'conflict-identifier-mod04',
      title: 'Conflict Identifier',
      subtitle: 'Solvable vs Perpetual Problems',
      description: 'Gottman estimates 69% of relationship conflicts are perpetual: they never get "solved." Learn to identify which conflicts are solvable vs which require ongoing dialogue and acceptance.',
      icon: Scale,
      heroImage: '/images/tools/conflict-identifier.jpg',
      color: 'from-amber-500 to-yellow-500',
      category: 'Module 4',
      estimatedTime: '10 mins',
      component: ConflictIdentifier,
      module: 4,
      priority: 1,
      features: [
        'Solvable vs perpetual conflict framework',
        'Underlying values and dreams exploration',
        'Dialogue approach for perpetual problems',
        'Track your conflicts and resolution approaches'
      ],
      whenToUse: 'When the same conflicts keep recurring and you\'re not sure if you\'re solving the wrong problem'
    },
    {
      id: 'flooding-monitor-mod04',
      title: 'Flooding Monitor',
      subtitle: 'Recognise Physiological Overwhelm',
      description: 'Gottman treats a heart rate above about 100bpm as a sign of flooding, when calm conversation becomes very hard. Learn to recognise flooding in yourself and your partner, and take effective timeouts before damage is done.',
      icon: Wind,
      heroImage: '/images/tools/flooding-monitor.jpg',
      color: 'from-cyan-500 to-blue-500',
      category: 'Module 4',
      estimatedTime: '5 mins',
      component: FloodingMonitor,
      module: 4,
      priority: 2,
      features: [
        'Flooding signs and symptoms',
        'Self-soothing techniques that actually work',
        'Effective timeout protocols',
        'Track your flooding patterns and triggers'
      ],
      whenToUse: 'When you or your partner become physiologically overwhelmed during conflict'
    },
    {
      id: 'repair-builder-mod04',
      title: 'Repair Builder',
      subtitle: 'Master the Art of Repair',
      description: 'Repair attempts (small gestures that de-escalate conflict) are the difference between couples who recover from fights vs those who spiral. Build your repair repertoire and learn to recognise repair when offered.',
      icon: Wrench,
      heroImage: '/images/tools/repair-builder.jpg',
      color: 'from-green-500 to-emerald-500',
      category: 'Module 4',
      estimatedTime: '10 mins',
      component: RepairBuilder,
      module: 4,
      priority: 3,
      features: [
        'Six categories of repair attempts',
        'Build personalised repair phrases',
        'Practice recognising repair when offered',
        'Track successful repairs and what works for you'
      ],
      whenToUse: 'When conflicts escalate and you need tools to de-escalate and reconnect'
    },

    // ===== MODULE 05: Intimacy & Vulnerability =====
    {
      id: 'five-dimensions-tracker-mod05',
      title: 'Five Dimensions Tracker',
      subtitle: 'Track Holistic Intimacy',
      description: 'Intimacy isn\'t just physical. It\'s emotional, intellectual, experiential, and spiritual too. Track all five dimensions to understand where you\'re connected and where you\'re drifting.',
      icon: Layers,
      heroImage: '/images/tools/five-dimensions.jpg',
      color: 'from-purple-500 to-pink-500',
      category: 'Module 5',
      estimatedTime: '5-10 mins/week',
      component: FiveDimensionsTracker,
      module: 5,
      priority: 1,
      features: [
        'Track all five intimacy dimensions (emotional, physical, intellectual, experiential, spiritual)',
        'Weekly check-ins with dimension-specific prompts',
        'Identify which dimensions need attention',
        'Visualise intimacy balance over time'
      ],
      whenToUse: 'Weekly check-ins to maintain balanced intimacy across all five dimensions'
    },
    {
      id: 'vulnerability-coach-mod05',
      title: 'Vulnerability Coach',
      subtitle: "Pace Your Vulnerability",
      description: 'Vulnerability builds intimacy, but only when it\'s paced well. Drawing on Brené Brown\'s idea that people earn the right to hear your story: choose who hears it, share a little at a time, and watch for reciprocity. Avoid the "vulnerability hangover."',
      icon: HeartHandshake,
      heroImage: '/images/tools/vulnerability-coach.jpg',
      color: 'from-rose-500 to-pink-500',
      category: 'Module 5',
      estimatedTime: '10-15 mins',
      component: VulnerabilityCoach,
      module: 5,
      priority: 2,
      features: [
        'Vulnerability calibration framework (who, what, how much, when)',
        'Practice scenarios with reciprocity tracking',
        'Identify vulnerability barriers',
        'Track vulnerability exchanges and outcomes'
      ],
      whenToUse: 'When you want to deepen intimacy through vulnerability but aren\'t sure how much to share or when'
    },
    {
      id: 'desire-navigator-mod05',
      title: 'Desire Navigator',
      subtitle: 'Navigate Desire Discrepancy',
      description: 'Most couples face desire discrepancy at some point. Navigate it without shame or pressure. Understand responsive vs spontaneous desire, and build approaches that honour both partners.',
      icon: Flame,
      heroImage: '/images/tools/desire-navigator.jpg',
      color: 'from-orange-500 to-red-500',
      category: 'Module 5',
      estimatedTime: '15 mins',
      component: DesireNavigator,
      module: 5,
      priority: 3,
      features: [
        'Responsive vs spontaneous desire framework',
        'Desire discrepancy dialogue prompts',
        'Pressure-free intimacy building approaches',
        'Track desire patterns and what works for both partners'
      ],
      whenToUse: 'When desire discrepancy is creating distance or pressure in your relationship'
    },

    // ===== MODULE 06: Trust =====
    {
      id: 'trust-builder-mod06',
      title: 'Trust Builder',
      subtitle: "Track Five Trust Components",
      description: 'Trust isn\'t just about fidelity. It\'s built through transparency, reliability, attunement, non-defensiveness, and selective sharing. Track these five dimensions and build trust deposits daily.',
      icon: Shield,
      heroImage: '/images/tools/trust-builder.jpg',
      color: 'from-blue-500 to-cyan-500',
      category: 'Module 6',
      estimatedTime: '5-10 mins/day',
      component: TrustBuilder,
      module: 6,
      priority: 1,
      features: [
        'Track all 5 trust components (transparency, reliability, attunement, non-defensive, selective sharing)',
        'Daily trust-building actions tracker',
        'Trust metric visualisation over time',
        'Build trust bank account systematically'
      ],
      whenToUse: 'Daily tracking to build and maintain trust through consistent small actions'
    },
    {
      id: 'trust-erosion-warning-mod06',
      title: 'Trust Erosion Warning',
      subtitle: 'Monitor Trust Warning Signs',
      description: 'Trust erodes slowly through small betrayals. Learn to recognise warning signs across five categories: secretive behaviour, inconsistency, emotional distance, small disappointments, and dismissive responses.',
      icon: AlertTriangle,
      heroImage: '/images/tools/trust-warning.jpg',
      color: 'from-amber-500 to-orange-500',
      category: 'Module 6',
      estimatedTime: '10 mins',
      component: TrustErosionWarning,
      module: 6,
      priority: 2,
      features: [
        'Monitor warning signs across 5 categories',
        'Gut feeling tracker for intuition',
        'Early intervention strategies',
        'Pattern recognition over time'
      ],
      whenToUse: 'When you sense something is off and want to identify specific concerns rather than vague unease'
    },
    {
      id: 'trust-agreement-builder-mod06',
      title: 'Trust Agreement Builder',
      subtitle: 'Build Clear Trust Agreements',
      description: 'Prevent trust breaches by building clear agreements upfront. Create specific, measurable commitments across communication, boundaries, finances, family, and personal growth. Track compliance and adjust as needed.',
      icon: FileText,
      heroImage: '/images/tools/trust-agreements.jpg',
      color: 'from-purple-500 to-pink-500',
      category: 'Module 6',
      estimatedTime: '20-30 mins',
      component: TrustAgreementBuilder,
      module: 6,
      priority: 3,
      features: [
        'Agreement templates across 5 categories',
        'Create custom agreements together',
        'Weekly compliance tracking',
        'Review and adjust agreements as relationship evolves'
      ],
      whenToUse: 'When you want to build explicit agreements to prevent trust issues and misunderstandings'
    },

    // ===== MODULE 07: Growing Together =====
    {
      id: 'growth-support-navigator-mod07',
      title: 'Growth Support Navigator',
      subtitle: 'Balance Interdependence & Growth',
      description: 'Healthy relationships balance togetherness with individual growth. Track interdependence, support each other\'s goals, and navigate major life transitions together whilst maintaining your sense of self.',
      icon: TrendingUp,
      heroImage: '/images/tools/growth-support.jpg',
      color: 'from-emerald-500 to-teal-500',
      category: 'Module 7',
      estimatedTime: '15-20 mins',
      component: GrowthSupportNavigator,
      module: 7,
      priority: 1,
      features: [
        'Interdependence balance tracker',
        'Individual growth goals with partner support tracking',
        'Life transition navigator (becoming parents, relocation, career change, etc.)',
        'Track how you support each other\'s development'
      ],
      whenToUse: 'When navigating individual growth or major life transitions whilst staying connected'
    },
    {
      id: 'shared-vision-builder-mod07',
      title: 'Shared Vision Builder',
      subtitle: 'Create Your Relationship Vision',
      description: 'Build a shared vision across 8 life areas: career, family, lifestyle, finances, social life, health, values, and legacy. Identify alignment and misalignment, then create a vision that honours both partners.',
      icon: Map,
      heroImage: '/images/tools/shared-vision.jpg',
      color: 'from-indigo-500 to-purple-500',
      category: 'Module 7',
      estimatedTime: '30-45 mins',
      component: SharedVisionBuilder,
      module: 7,
      priority: 2,
      features: [
        'Individual vision exploration across 8 life areas',
        'Alignment analysis: where you agree and differ',
        'Build shared vision that honours both partners',
        'Track progress toward shared goals'
      ],
      whenToUse: 'When you want clarity about where you\'re headed together and how your individual visions align'
    },

    // ===== MODULE 08: Resilience =====
    {
      id: 'relationship-compass-mod08',
      title: 'Relationship Compass',
      subtitle: 'Visualise All 8 Dimensions',
      description: 'Your relationship dashboard showing all 8 dimensions: appreciation, emotions, communication, conflict, intimacy, trust, growth, and resilience. See the big picture and identify which areas need attention.',
      icon: Compass,
      heroImage: '/images/tools/relationship-compass.jpg',
      color: 'from-rose-500 to-pink-500',
      category: 'Module 8',
      estimatedTime: '5 mins',
      component: RelationshipCompass,
      module: 8,
      priority: 1,
      features: [
        'Visual dashboard of all 8 relationship dimensions',
        'Data pulled from all previous modules and tools',
        'Identify strengths and growth areas',
        'Track overall relationship health over time'
      ],
      whenToUse: 'Regular check-ins to see the big picture of your relationship health'
    },
    {
      id: 'rituals-builder-mod08',
      title: 'Rituals Builder',
      subtitle: 'Build Relationship Rituals',
      description: 'Resilient relationships have rituals: daily, weekly, monthly, and annual touchpoints that keep you connected. Build rituals from templates or create your own, then track consistency.',
      icon: Calendar,
      heroImage: '/images/tools/rituals-builder.jpg',
      color: 'from-violet-500 to-purple-500',
      category: 'Module 8',
      estimatedTime: '15 mins',
      component: RitualsBuilder,
      module: 8,
      priority: 2,
      features: [
        'Daily, weekly, monthly, and annual ritual templates',
        'Create custom rituals that fit your lifestyle',
        'Track ritual completion and consistency',
        'Build the rhythm that sustains connection'
      ],
      whenToUse: 'When you want to build intentional touchpoints that keep you connected through life\'s busyness'
    },
    {
      id: 'resilience-navigator-mod08',
      title: 'Resilience Navigator',
      subtitle: 'Daily Practices & Crisis Management',
      description: 'Build daily resilience through gratitude, emotional connection, physical touch, and conflict skills. When crisis hits, navigate it with structured support rather than reactive panic.',
      icon: Shield,
      heroImage: '/images/tools/resilience-navigator.jpg',
      color: 'from-cyan-500 to-blue-500',
      category: 'Module 8',
      estimatedTime: '10-15 mins',
      component: ResilienceNavigator,
      module: 8,
      priority: 3,
      features: [
        'Daily resilience practices tracker (gratitude, emotional connection, physical touch, conflict skills)',
        'Crisis management framework for major challenges',
        'Track practice consistency and relationship buffer',
        'Build the resilience that helps you weather any storm'
      ],
      whenToUse: 'Daily practice for building resilience, crisis framework when major challenges hit'
    }
  ];

  // Sort tools by module order
  const sortedTools = [...tools].sort((a, b) => {
    if (a.module === b.module) return a.priority - b.priority;
    return a.module - b.module;
  });

  const ToolCard = ({ tool }) => {
    const stats = toolsUsage[tool.id] || { uses: 0, lastUsed: null };
    const isComingSoon = !tool.component;

    return (
      <Card
        hover={!isComingSoon}
        className={`p-4 sm:p-6 h-full ${!isComingSoon ? 'cursor-pointer' : 'opacity-75'}`}
        onClick={() => !isComingSoon && setActiveTool(tool.id)}
      >
        <div className="space-y-3 sm:space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center bg-gradient-to-br ${tool.color} flex-shrink-0`}>
              <tool.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>

            <div className="text-right">
              <span className="text-xs text-slate-400">{tool.category}</span>
              {isComingSoon && (
                <div className="text-xs text-amber-400 mt-1">
                  Coming Soon
                </div>
              )}
              {stats.uses > 0 && (
                <div className="text-xs text-green-400 mt-1">
                  Used {stats.uses}x
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{tool.title}</h3>
            <p className="text-xs sm:text-sm text-rose-200 font-medium mb-2 sm:mb-3">{tool.subtitle}</p>
            <p className="text-slate-300 text-xs sm:text-sm mb-3 sm:mb-4">{tool.description}</p>

            {/* When to Use */}
            <div className="p-2 sm:p-3 bg-rose-500/10 rounded-lg mb-3 sm:mb-4">
              <p className="text-xs text-rose-300 font-medium mb-1">When to Use:</p>
              <p className="text-xs text-slate-300">{tool.whenToUse}</p>
            </div>

            {/* Features */}
            <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
              {tool.features.slice(0, 2).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-400">
                  <div className="w-1 h-1 bg-rose-400 rounded-full" />
                  {feature}
                </div>
              ))}
              {tool.features.length > 2 && (
                <div className="text-xs text-slate-400">
                  +{tool.features.length - 2} more features
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-slate-700">
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                {tool.estimatedTime}
              </span>

              <Button size="sm" variant="ghost" className="text-xs sm:text-sm" disabled={isComingSoon}>
                <span className="hidden sm:inline">{isComingSoon ? 'Coming Soon' : 'Open Tool'}</span>
                <span className="sm:hidden">{isComingSoon ? 'Soon' : 'Open'}</span>
                {!isComingSoon && <ArrowRight className="w-3 h-3 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  if (activeTool) {
    const tool = tools.find(t => t.id === activeTool);
    const ToolComponent = tool.component;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 pt-24 md:pt-32">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="max-w-6xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => setActiveTool(null)}
              className="flex items-center gap-2 text-rose-300 hover:text-white mb-6 sm:mb-8 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Toolkit
            </button>

            {/* Tool Component */}
            <Suspense fallback={
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                  <p className="text-slate-400">Loading tool...</p>
                </div>
              </div>
            }>
              <ToolComponent />
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 pt-24 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-12"
          >
            {/* Hero Image */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-block"
            >
              <img
                src="/images/toolkit.png"
                alt="Toolkit"
                className="w-60 h-60 md:w-80 md:h-80 object-contain rounded-2xl mx-auto"
              />
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">
              Your Relationship
              <span className="block bg-gradient-to-r from-rose-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                Practice Toolkit
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 max-w-2xl mx-auto mb-4 sm:mb-6 px-4">
              23 interactive tools that take relationship skills from theory to practice. Track progress, build habits, and strengthen your connection, one tool at a time.
            </p>
            <p className="text-base sm:text-lg text-slate-300 max-w-3xl mx-auto px-4">
              These draw on the work of Gottman, Brown, Perel and Johnson. They aren't gimmicks; they're genuine skill-builders you can use today.
            </p>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12"
          >
            <Card className="p-3 sm:p-4 text-center">
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {Object.values(toolsUsage).reduce((sum, stat) => sum + (stat.uses || 0), 0)}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Total Tool Uses</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {Object.keys(toolsUsage).length}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Tools Explored</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {toolsUsage['appreciation-builder-mod01']?.appreciationsCreated || 0}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Appreciations Built</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Target className="w-6 h-6 sm:w-8 sm:h-8 text-rose-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">{tools.length}</div>
              <div className="text-xs sm:text-sm text-slate-400">Total Tools</div>
            </Card>
          </motion.div>

          {/* Tools Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12"
          >
            {sortedTools.map((tool, idx) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx }}
              >
                <ToolCard tool={tool} />
              </motion.div>
            ))}
          </motion.div>

          {/* Usage Tips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="p-4 sm:p-6 md:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center">
                How to Use Your Toolkit
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Build Your Foundation</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Start with Module 1 tools (Appreciation Builder, 5:1 Ratio Tracker) to build the positivity that sustains relationships through challenges.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Develop Specific Skills</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Use targeted tools like the Four Horsemen Detector and LAER Trainer to build specific communication and conflict resolution skills.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Deepen & Sustain</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Use Module 5 tools (Five Dimensions Tracker, Vulnerability Coach) to deepen intimacy and maintain connection across all dimensions.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ToolkitPage;
